import { mergeMap, catchError } from 'rxjs/operators'
import { NODE_TYPE } from 'enums'
import { of } from 'rxjs'
import Fetch from '../fetch'
import UserPreferencesApi from '../UserPreferencesApi'

/**
 * Deal with Favourites action (add/remove/get)
 */
export default class FavouritesApi extends Fetch {
    constructor(config, otherApis = {}) {
        super(config, otherApis)
        this.userPreferencesApi = otherApis.userPreferencesApi
        this.proxyApi = otherApis.proxyApi
    }
    /**
     * Get All favourites (both Movies/Titles and Series)
     * @return {Observable<Array<String>>} result list of program IDs
     */
    getAllVODs() {
        if (this.config.useNagraLegacyData) {
            return this.userPreferencesApi
                .getBookmarks(UserPreferencesApi.WATCHLIST_AND_RENTALS)
                .pipe(
                    mergeMap((bookmarks) => {
                        const _getBookmarkId = (bookmark) => {
                            return bookmark.contentId
                                .split(UserPreferencesApi.WATCHLIST_AND_RENTALS_PREFIX)
                                .pop()
                        }

                        // We need to separate `seriesBookmarks` from `moviesBookmarks`, because if `useNagraLegacyData` is true,
                        // then `seriesBookmarks` should be transformed to be correctly handled
                        // Example: `GLOBAL_ND0000007980`=> `GLOBAL_ND0000007980_Series`
                        const [seriesBookmarks, moviesBookmarks] = bookmarks.reduce(
                            (acc, bookmark, index) =>
                                bookmark.metadata?.type === NODE_TYPE.SERIESNODE
                                    ? (acc[0].push({ id: _getBookmarkId(bookmark), index }), acc)
                                    : (acc[1].push({ id: _getBookmarkId(bookmark), index }), acc),
                            [[], []]
                        )

                        // There are only movies
                        if (!seriesBookmarks.length) {
                            return of(moviesBookmarks.map((b) => b.id))
                        }

                        // There are series
                        return this.proxyApi
                            .getSeriesIdFromNodePlatformID({
                                id: seriesBookmarks.map((b) => b.id),
                            })
                            .pipe(
                                mergeMap((updatedSeriesBookmarks) => {
                                    if (!moviesBookmarks.length) {
                                        return of(updatedSeriesBookmarks)
                                    }

                                    // We need to re-order all the bookmarks
                                    // First, we have to add the correct index for each updated seriesBookmarks
                                    const updatedSeriesBookmarksWithIndex = updatedSeriesBookmarks
                                        .filter(Boolean)
                                        .map((bookmark) => ({
                                            id: bookmark,
                                            index: seriesBookmarks.find((b) =>
                                                bookmark.includes(b.id)
                                            ).index,
                                        }))

                                    const sortArrayOfObjects = (arr, propertyName) => {
                                        const sortedArr = arr.sort((a, b) => {
                                            if (a[propertyName] < b[propertyName]) {
                                                return -1
                                            }
                                            if (a[propertyName] > b[propertyName]) {
                                                return 1
                                            }
                                            return 0
                                        })
                                        return sortedArr
                                    }

                                    // Then, concat the series and moviesBookmarks and re-order them
                                    const sortedBookmarks = sortArrayOfObjects(
                                        updatedSeriesBookmarksWithIndex.concat(moviesBookmarks),
                                        'index'
                                    )

                                    return of(sortedBookmarks.map((b) => b.id))
                                }),
                                catchError((error) => {
                                    this.logger.warn(
                                        'Series bookmarks can not be retrieved, error = ',
                                        error
                                    )
                                    // updatedSeriesBookmarks can not be retrieved, so we return only moviesBookmarks
                                    return of(moviesBookmarks.map((b) => b.id))
                                })
                            )
                    })
                )
        }
        return this.userPreferencesApi.getItemsByCollectionName(
            UserPreferencesApi.FAVOURITES_LIST_NAMES.VOD
        )
    }

    /**
     * Add favourite VOD
     * @param {Object} program
     * @param {String} program.id - id of the favourite
     * @param {Object} program.nodesPlatformIds
     * @param {String} isSeries - is this VOD a serie or not
     * @param {Number} expiresDate - end of validity
     * @return {Observable<Object>} returns added favourite details
     */
    addFavouriteVOD({ id, nodesPlatformIds, isSeries, expiresDate }) {
        if (this.config.useNagraLegacyData) {
            const _id = isSeries && nodesPlatformIds ? nodesPlatformIds[0] : id
            if (isSeries && !nodesPlatformIds) {
                this.logger.warn('addFavouriteVOD - nodesPlatformIds is missing')
            }

            return this.userPreferencesApi.addBookmark(
                0, //position
                `${UserPreferencesApi.WATCHLIST_AND_RENTALS_PREFIX}${_id}`,
                UserPreferencesApi.WATCHLIST_AND_RENTALS,
                {
                    expiresDate: expiresDate,
                    type: isSeries && nodesPlatformIds ? 'SeriesNode' : 'Editorial',
                }, // TO DO. How to set correct value
                [UserPreferencesApi.WATCHLIST_AND_RENTALS]
            )
        }

        return this.userPreferencesApi.addCollectionItem(
            UserPreferencesApi.FAVOURITES_LIST_NAMES.VOD,
            id
        )
    }

    /**
     * Remove item from favourites
     * @param {String} program.id - id of the favourite
     * @param {Object} program.nodesPlatformIds
     * @param {String} isSeries - is this VOD a serie or not
     * @return {Observable<Boolean>} returns true if removing was successful
     */
    removeFavouriteVOD({ id, nodesPlatformIds, isSeries }) {
        if (this.config.useNagraLegacyData) {
            const _id = isSeries && nodesPlatformIds ? nodesPlatformIds[0] : id
            if (isSeries && !nodesPlatformIds) {
                this.logger.warn('removeFavouriteVOD - nodesPlatformIds is missing')
            }

            return this.userPreferencesApi.deleteBookmark(`WATCHLIST_AND_RENTALS_${_id}`)
        }

        return this.userPreferencesApi.deleteCollectionItem(
            UserPreferencesApi.FAVOURITES_LIST_NAMES.VOD,
            id
        )
    }

    /**
     * Recursively fetches all favorite recordings
     *
     * @param {Object} params
     * @param {Number} [params.limit=20] The maximum number of results per fetch
     * @returns {Observable<Array<Dvr>>}
     */
    getFavoriteRecordings({ limit = 20 } = {}) {
        return this.userPreferencesApi.getItemsByCollectionName(
            UserPreferencesApi.FAVOURITES_LIST_NAMES.DVR
        )
    }

    /**
     * Adds a recording to favourites
     * @param {Object} params
     * @param {Boolean} params.id - Recording ID
     * @returns {Observable<Object>} returns added DVR details
     */
    addFavoriteRecording({ id }) {
        return this.userPreferencesApi.addCollectionItem(
            UserPreferencesApi.FAVOURITES_LIST_NAMES.DVR,
            id
        )
    }

    /**
     * Removes a recording from favourites
     * @param {Object} params
     * @param {Boolean} params.id - Recording ID
     * @returns {Observable<Boolean>} returns true if removing was successful
     */
    deleteFavoriteRecording({ id }) {
        return this.userPreferencesApi.deleteCollectionItem(
            UserPreferencesApi.FAVOURITES_LIST_NAMES.DVR,
            id
        )
    }
}
